/**
 * 定盘流派对照卡
 *
 * 规则（方案约束）：
 *   - 选择后无二次确认，直接进入报告流式生成
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import type { CalibrationCandidate } from '@guanwei/shared';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

interface CalibrationCardsProps {
  candidates: CalibrationCandidate[];
  fallback: boolean;
  onSelect: (schoolId: string) => void;
  loading?: boolean;
}

export function CalibrationCards({
  candidates,
  fallback,
  onSelect,
  loading,
}: CalibrationCardsProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [submittingId, setSubmittingId] = useState<string | null>(null);

  const pick = (id: string) => {
    if (submittingId) return;
    setSubmittingId(id);
    window.setTimeout(() => onSelect(id), 520);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="font-serif text-2xl text-fg">观微先生 · 定盘问询</h2>
          <p className="text-sm text-fg-2 mt-1">
            主流派对此局有不同切入视角，请根据你更认可的判定路径选择
          </p>
        </div>
        {fallback && (
          <span className="text-xs text-warning">
            * 当前为通用兜底卡（模型未返回合规 JSON，已使用临时卡片）
          </span>
        )}
      </div>

      <div
        className={clsx(
          'grid gap-4',
          candidates.length === 2
            ? 'grid-cols-1 md:grid-cols-2'
            : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
        )}
      >
        {candidates.map((c, idx) => (
          <motion.div
            key={c.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.08 }}
            onMouseEnter={() => setHoveredId(c.id)}
            onMouseLeave={() => setHoveredId(null)}
          >
            <Card
              variant={hoveredId === c.id ? 'gold' : 'default'}
              className={clsx(
                'relative h-full overflow-hidden p-6 flex flex-col transition-all',
                submittingId === c.id && 'border-border-accent shadow-gold',
                submittingId && submittingId !== c.id && 'opacity-45',
              )}
            >
              {submittingId === c.id && (
                <motion.div
                  initial={{ opacity: 0, scale: 1.4, rotate: -12 }}
                  animate={{ opacity: 1, scale: 1, rotate: -12 }}
                  className="pointer-events-none absolute right-5 top-5 rounded-md border border-accent px-3 py-1 font-serif text-sm text-accent"
                >
                  已锁定
                </motion.div>
              )}
              <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-border-accent" />
              <div className="mb-3">
                <span className="text-xs text-fg-muted">{c.school}</span>
              </div>

              <h3 className="font-serif text-xl text-fg leading-snug mb-3">{c.headline}</h3>

              <p className="text-sm text-fg-1 leading-relaxed mb-5 flex-1">{c.summary}</p>

              <Button onClick={() => pick(c.id)} disabled={loading || submittingId !== null}>
                {submittingId === c.id ? '判定路径已锁定' : '采用此判定'}
              </Button>
            </Card>
          </motion.div>
        ))}
      </div>

      <p className="text-xs text-fg-muted text-center mt-2">
        * 观微不对未来作必然预测；卡片仅为不同流派的判定路径，供参考
      </p>
    </div>
  );
}
